import { useState, useEffect, useCallback } from 'react';
import { API_TIMEOUT, buildApiUrl } from '../config';

const apiRequest = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  const config = {
    headers: { ...defaultHeaders, ...options.headers },
    ...options
  };
  
  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.mensaje || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      data,
      error: data.error,
      mensaje: data.mensaje
    };
    
  } catch (error) {
    throw error;
  }
};

export const usePromociones = (options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    immediate = false
  } = options;

  const fetchPromociones = useCallback(async () => {
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(buildApiUrl('promociones'), {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      });

      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const validatedData = result.data?.promociones || result.data || result;
      setData(validatedData);
      return validatedData;
    } catch (err) {
      const errorMessage = err.name === 'AbortError' 
        ? 'Tiempo de espera agotado' 
        : 'Error de conexión con el servidor';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPromocionesActivas = useCallback(async () => {
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(buildApiUrl('promociones/activas'), {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      });

      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data || result;
    } catch (err) {
      const errorMessage = err.name === 'AbortError' 
        ? 'Tiempo de espera agotado' 
        : 'Error de conexión con el servidor';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (immediate) {
      fetchPromociones();
    }
  }, [immediate, fetchPromociones]);

  return {
    data,
    loading,
    error,
    refetch: fetchPromociones,
    refetchActivas: fetchPromocionesActivas,
  };
};

export const getPromocionById = async (id) => {
  const result = await apiRequest(`promocion/${id}`, { method: 'GET' });
  return result;
};

export const createPromocion = async (promocionData) => {
  const dataToSend = {
    pTitulo: promocionData.Titulo || promocionData.pTitulo,
    pFecha_inicio: promocionData.Fecha_inicio || promocionData.pFecha_inicio,
    pFecha_fin: promocionData.Fecha_fin || promocionData.pFecha_fin,
    pUbicacion: promocionData.Ubicacion || promocionData.pUbicacion,
    pMes: promocionData.Mes || promocionData.pMes,
    pDescripcion: promocionData.Descripcion || promocionData.pDescripcion,
    pValor_descuento: promocionData.Valor_descuento || promocionData.pValor_descuento,
    pEstado: promocionData.Estado || promocionData.pEstado,
    pUrl_File: promocionData.Url_File || promocionData.pUrl_File,
    pFileName: promocionData.FileName || promocionData.pFileName
  };
  return apiRequest('promocion', {
    method: 'POST',
    body: JSON.stringify(dataToSend)
  });
};

export const updatePromocion = async (id, promocionData) => {
  const dataToSend = {
    pTitulo: promocionData.Titulo || promocionData.pTitulo,
    pFecha_inicio: promocionData.Fecha_inicio || promocionData.pFecha_inicio,
    pFecha_fin: promocionData.Fecha_fin || promocionData.pFecha_fin,
    pUbicacion: promocionData.Ubicacion || promocionData.pUbicacion,
    pMes: promocionData.Mes || promocionData.pMes,
    pDescripcion: promocionData.Descripcion || promocionData.pDescripcion,
    pValor_descuento: promocionData.Valor_descuento || promocionData.pValor_descuento,
    pEstado: promocionData.Estado || promocionData.pEstado,
    pUrl_File: promocionData.Url_File || promocionData.pUrl_File,
    pFileName: promocionData.FileName || promocionData.pFileName
  };
  return apiRequest(`promocion/${id}`, {
    method: 'PUT',
    body: JSON.stringify(dataToSend)
  });
};

export const deletePromocion = async (id) => {
  return apiRequest('eliminarPromocion', {
    method: 'POST',
    body: JSON.stringify({ pIdPromocion: id })
  });
};

export const validarCupon = async (codigoPromo) => {
  return apiRequest('cupones/validar', {
    method: 'POST',
    body: JSON.stringify({ pCodigoPromo: codigoPromo })
  });
};

export default {
  usePromociones,
  getPromocionById,
  createPromocion,
  updatePromocion,
  deletePromocion,
  validarCupon
};
